import {
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  VStack,
  Box,
  Text,
  Spinner,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import api from "../../api";
import {
  Autobooking,
  CreateAutobookingRequest,
  UpdateAutobookingRequest,
  User,
  UserRole,
} from "../../api/types";
import useFetchCourts from "../../hooks/api/useFetchCourts";
import useSearchUsers from "../../hooks/api/useSearchUsers";
import useShowToast from "../../hooks/useShowToast";

type AutobookingFormData = {
  day: number;
  start_time: string;
  number_of_hours: number;
  user_id: number | null;
  username: string | null;
  court_id: number;
  is_double: boolean;
  is_active: boolean;
};

type SettingsAutomaticBookingsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  autobooking?: Autobooking | null;
};

const SettingsAutomaticBookingsModal = ({
  isOpen,
  onClose,
  autobooking = null,
}: SettingsAutomaticBookingsModalProps) => {
  const isEditMode = autobooking != null;
  const { t } = useTranslation();
  const { successToast, errorToast } = useShowToast();
  const { courts } = useFetchCourts();
  const {
    users: searchResults,
    isLoading: isSearching,
    searchUsers,
    clearResults,
  } = useSearchUsers();
  const [isLoading, setIsLoading] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState("");

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<AutobookingFormData>();

  const guestName = userSearchQuery.trim();
  const selectedGuest = watch("username");
  const canNameAGuest = guestName.length >= 2 && !selectedGuest && watch("user_id") == null;

  const handleUserSearch = (query: string) => {
    setValue("user_id", null);
    setValue("username", null);
    setUserSearchQuery(query);
    if (query.trim().length >= 2) {
      searchUsers(query);
    } else {
      clearResults();
    }
  };

  const handleUserSelect = (user: User) => {
    setValue("user_id", user.id);
    setValue("username", null);
    setUserSearchQuery(`${user.username}`);
    clearResults();
  };

  const handleGuestSelect = () => {
    setValue("user_id", null);
    setValue("username", guestName);
    setUserSearchQuery(guestName);
    clearResults();
  };

  const onSubmit = async (data: AutobookingFormData) => {
    if (data.user_id == null && !data.username) {
      errorToast({
        title: t("settings.automatic_bookings.error_title"),
        description: t("settings.automatic_bookings.error_message.missing_user"),
      });
      return;
    }

    setIsLoading(true);

    if (isEditMode && autobooking) {
      const request: UpdateAutobookingRequest = {
        day: data.day,
        start_time: data.start_time,
        number_of_hours: data.number_of_hours,
        user_id: data.user_id ?? undefined,
        username: data.username ?? undefined,
        court_id: data.court_id,
        is_double: data.is_double,
        is_active: data.is_active,
      };
      await api.updateAutobooking(autobooking.id, request);
    } else {
      const request: CreateAutobookingRequest = {
        day: data.day,
        start_time: data.start_time,
        number_of_hours: data.number_of_hours,
        user_id: data.user_id ?? undefined,
        username: data.username ?? undefined,
        court_id: data.court_id,
        is_double: data.is_double,
        is_active: true,
      };
      await api.createAutobooking(request);
    }

    if (api.error) {
      setIsLoading(false);
      errorToast({
        title: t("settings.automatic_bookings.error_title"),
        description: t("settings.automatic_bookings.error_message.default"),
      });
      return;
    }
    setIsLoading(false);
    onClose();
    resetForm();

    successToast({
      title: t("settings.automatic_bookings.success_title"),
      description: t("settings.automatic_bookings.success_message"),
    });
  };

  const resetForm = useCallback(() => {
    reset({
      day: 0,
      start_time: "09:00",
      number_of_hours: 1,
      user_id: null,
      username: null,
      court_id: courts.length > 0 ? courts[0].id : 0,
      is_double: false,
      is_active: true,
    });
    setUserSearchQuery("");
  }, [reset, courts]);

  useEffect(() => {
    if (autobooking) {
      reset({
        day: autobooking.day,
        start_time: autobooking.start_time,
        number_of_hours: autobooking.number_of_hours,
        user_id: autobooking.user.is_guest ? null : autobooking.user.id,
        username: autobooking.user.is_guest ? autobooking.user.username : null,
        court_id: autobooking.court.id,
        is_double: autobooking.is_double,
        is_active: autobooking.is_active,
      });
      setUserSearchQuery(autobooking.user.username);
    } else {
      resetForm();
    }
  }, [isOpen, autobooking, reset, resetForm]);

  const suggestionGroups = [
    {
      label: t("settings.automatic_bookings.modal.users_group"),
      users: searchResults.filter(user => user.role !== UserRole.SERVICE),
      optionLabel: (user: User) => `${user.username} (${user.name} ${user.surname})`,
    },
    {
      label: t("settings.automatic_bookings.modal.service_hours_group"),
      users: searchResults.filter(user => user.role === UserRole.SERVICE),
      optionLabel: (user: User) => user.username,
    },
  ];

  const dayOptions = [
    { value: 0, label: t("settings.automatic_bookings.days.0") },
    { value: 1, label: t("settings.automatic_bookings.days.1") },
    { value: 2, label: t("settings.automatic_bookings.days.2") },
    { value: 3, label: t("settings.automatic_bookings.days.3") },
    { value: 4, label: t("settings.automatic_bookings.days.4") },
    { value: 5, label: t("settings.automatic_bookings.days.5") },
    { value: 6, label: t("settings.automatic_bookings.days.6") },
  ];

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
        <ModalOverlay />
        <ModalContent maxW="600px">
          {/* Modal title */}
          <ModalHeader>
            {isEditMode
              ? t("settings.automatic_bookings.modal.edit_title")
              : t("settings.automatic_bookings.modal.add_title")}
          </ModalHeader>

          <Divider />

          {/* Modal Body */}
          <ModalBody>
            <form onSubmit={handleSubmit(onSubmit)}>
              <VStack spacing={4}>
                <FormControl isRequired isInvalid={!!errors.day}>
                  <FormLabel>{t("settings.automatic_bookings.modal.day")}</FormLabel>
                  <Select
                    {...register("day", { valueAsNumber: true })}
                    placeholder={t("settings.automatic_bookings.modal.day_placeholder")}
                  >
                    {dayOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.start_time}>
                  <FormLabel>{t("settings.automatic_bookings.modal.start_time")}</FormLabel>
                  <Input
                    type="time"
                    {...register("start_time")}
                    placeholder={t("settings.automatic_bookings.modal.start_time_placeholder")}
                    required
                  />
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.number_of_hours}>
                  <FormLabel>{t("settings.automatic_bookings.modal.number_of_hours")}</FormLabel>
                  <Input
                    type="number"
                    min={1}
                    max={24}
                    {...register("number_of_hours", { valueAsNumber: true })}
                    placeholder={t("settings.automatic_bookings.modal.number_of_hours_placeholder")}
                    required
                  />
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.user_id}>
                  <FormLabel>{t("settings.automatic_bookings.modal.user")}</FormLabel>
                  <Box position="relative">
                    <Input
                      value={userSearchQuery}
                      onChange={e => handleUserSearch(e.target.value)}
                      placeholder={t("settings.automatic_bookings.modal.user_placeholder")}
                      required
                    />
                    {isSearching && (
                      <Box position="absolute" right={2} top="50%" transform="translateY(-50%)">
                        <Spinner size="sm" />
                      </Box>
                    )}
                    {(searchResults.length > 0 || canNameAGuest) && (
                      <Box
                        position="absolute"
                        top="100%"
                        left={0}
                        right={0}
                        bg="white"
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                        boxShadow="lg"
                        zIndex={10}
                        maxHeight="200px"
                        overflowY="auto"
                      >
                        {suggestionGroups.map(group =>
                          group.users.length === 0 ? null : (
                            <Box key={group.label}>
                              <Text
                                px={2}
                                pt={2}
                                fontSize="xs"
                                fontWeight="semibold"
                                color="gray.500"
                                textTransform="uppercase"
                              >
                                {group.label}
                              </Text>
                              {group.users.map(user => (
                                <Box
                                  key={user.id}
                                  p={2}
                                  cursor="pointer"
                                  _hover={{ bg: "gray.100" }}
                                  onClick={() => handleUserSelect(user)}
                                >
                                  <Text fontSize="sm">{group.optionLabel(user)}</Text>
                                </Box>
                              ))}
                            </Box>
                          ),
                        )}
                        {canNameAGuest && (
                          <Box
                            p={2}
                            cursor="pointer"
                            _hover={{ bg: "gray.100" }}
                            borderTopWidth={searchResults.length > 0 ? "1px" : 0}
                            borderColor="gray.200"
                            onClick={handleGuestSelect}
                          >
                            <Text fontSize="sm">
                              {t("settings.automatic_bookings.modal.use_as_guest", {
                                username: guestName,
                              })}
                            </Text>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                  {selectedGuest && (
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      {t("settings.automatic_bookings.modal.guest_hint")}
                    </Text>
                  )}
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.court_id}>
                  <FormLabel>{t("settings.automatic_bookings.modal.court")}</FormLabel>
                  <Select
                    {...register("court_id", { valueAsNumber: true })}
                    placeholder={t("settings.automatic_bookings.modal.court_placeholder")}
                  >
                    {courts.map(court => (
                      <option key={court.id} value={court.id}>
                        {court.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <Checkbox
                    {...register("is_double")}
                    onChange={e => setValue("is_double", e.target.checked)}
                  >
                    {t("settings.automatic_bookings.modal.is_double")}
                  </Checkbox>
                </FormControl>

                {isEditMode && (
                  <FormControl>
                    <Checkbox
                      {...register("is_active")}
                      onChange={e => setValue("is_active", e.target.checked)}
                    >
                      {t("settings.automatic_bookings.modal.is_active")}
                    </Checkbox>
                  </FormControl>
                )}
              </VStack>
            </form>
          </ModalBody>

          {/* Modal Footer with buttons */}
          <ModalFooter>
            <Button variant="ghost" onClick={onClose}>
              {t("settings.automatic_bookings.modal.cancel")}
            </Button>

            <Button
              colorScheme="blue"
              ml={3}
              onClick={handleSubmit(onSubmit)}
              isLoading={isLoading}
            >
              {isEditMode
                ? t("settings.automatic_bookings.modal.edit")
                : t("settings.automatic_bookings.modal.add")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default SettingsAutomaticBookingsModal;
